/**
 * Prest0n Session — SSE Stream Processor
 *
 * Parses Server-Sent Events from the backend turn endpoint and
 * dispatches parsed events to the provided callbacks.
 *
 * SSE wire format (per W3C spec):
 *   event: <type>\n
 *   data: <JSON>\n
 *   \n
 *
 * The backend may also send plain `data:` lines without an explicit
 * `event:` field, in which case we fall back to parsing the `type`
 * field from the JSON data itself.
 */

import type { StreamEvent, TurnStreamCallbacks } from './types';
import { detectProviderFromModelId } from '../../config/providerCodeNames';
import { publishUsageWarning } from './usageWarningChannel';
import { readJobStatus } from '../../types/jobStatus';

// ============================================================================
// SSE Line Parser
// ============================================================================

interface ParsedSSEMessage {
  event?: string;
  data?: string;
}

/**
 * Parse a block of SSE text (lines up to a blank line) into event+data.
 */
function parseSSEBlock(block: string): ParsedSSEMessage | null {
  const lines = block.split('\n');
  let event: string | undefined;
  const dataLines: string[] = [];

  for (const line of lines) {
    if (line.startsWith('event:')) {
      event = line.slice('event:'.length).trim();
    } else if (line.startsWith('data:')) {
      dataLines.push(line.slice('data:'.length).trim());
    }
    // Ignore `id:`, `retry:`, and comment lines (`:`)
  }

  if (dataLines.length === 0) {
    return null;
  }

  return { event, data: dataLines.join('\n') };
}

/**
 * Parse a raw SSE data string into a typed StreamEvent.
 * Falls back to parsing `type` from the JSON if no explicit event field.
 */
function parseStreamEvent(message: ParsedSSEMessage): StreamEvent | null {
  if (!message.data) return null;

  let parsed: Record<string, unknown>;
  try {
    parsed = JSON.parse(message.data);
  } catch {
    // Not JSON — skip
    return null;
  }

  const eventType = message.event || (parsed.type as string | undefined);
  if (!eventType) return null;

  switch (eventType) {
    case 'thinking':
      return {
        type: 'thinking',
        data: { message: (parsed.data as any)?.message || (parsed.message as string | undefined) },
      };

    case 'text':
      return {
        type: 'text',
        data: {
          // WIRE-SHAPE FIX (2026-08-23): the dedicated-VM lane's translator
          // (prest0nVm/proxyClient) and the wake-reply endpoint both emit
          // `text` events carrying `delta`, never `chunk` — this case read
          // only `chunk`, so every VM-lane token resolved to '' and the
          // bubble was actually painted at finalize by `finalTextOverride`
          // (the glued-fragment symptom the R11 note below describes). The
          // CF runtime lane keeps sending `chunk`; accept both.
          chunk:
            (parsed.data as any)?.chunk
            || (parsed.data as any)?.delta
            || (parsed.chunk as string)
            || (parsed as any).delta
            || '',
          done: (parsed.data as any)?.done || (parsed.done as boolean | undefined),
        },
      };

    case 'tool_start':
      return {
        type: 'tool_start',
        data: {
          // 2026-08-12: the CF runtime lane (runtime.ts emit) ships the tool's
          // identity as `toolName` (it spreads the whole ToolResult / tool-call
          // into `data`), not `toolId` — without the fallback every CF-lane
          // tool event collapsed to 'unknown', which broke per-tool consumers
          // (e.g. the APP_NOT_CONNECTED connect-card auto-offer).
          toolId:
            (parsed.data as any)?.toolId ||
            (parsed.data as any)?.toolName ||
            (parsed.toolId as string) ||
            (parsed.toolName as string) ||
            'unknown',
          label: (parsed.data as any)?.label || (parsed.label as string) || 'Working...',
          context: (parsed.data as any)?.context || (parsed.context as string | undefined),
        },
      };

    case 'tool_result':
      return {
        type: 'tool_result',
        data: {
          // 2026-08-12: same `toolName` fallback as tool_start above.
          toolId:
            (parsed.data as any)?.toolId ||
            (parsed.data as any)?.toolName ||
            (parsed.toolId as string) ||
            (parsed.toolName as string) ||
            'unknown',
          success: (parsed.data as any)?.success ?? (parsed.success as boolean) ?? true,
          resultText: (parsed.data as any)?.resultText || (parsed.resultText as string | undefined),
          resultHtml: (parsed.data as any)?.resultHtml || (parsed.resultHtml as string | undefined),
          error: (parsed.data as any)?.error || (parsed.error as string | undefined),
          // 2026-08-19 (shadow mode): the structured twin of the error
          // markers — same spread-into-data transport as `error` above.
          signal: (parsed.data as any)?.signal || (parsed.signal as any) || undefined,
        },
      };

    case 'done':
      return {
        type: 'done',
        data: {
          handled: (parsed.data as any)?.handled ?? (parsed.handled as boolean) ?? true,
          tokenUsage: (parsed.data as any)?.tokenUsage || (parsed.tokenUsage as any),
          // Per-model breakdown for multi-model turns (tier ladders routinely
          // fall back mid-turn). Previously typed but never parsed, which
          // silently collapsed VM turns to a single usage row.
          tokenUsages: (parsed.data as any)?.tokenUsages || (parsed.tokenUsages as any) || undefined,
          // dr-compose-v2 — backend may now ship binary deliverables
          // (deep-research PDF + DOCX) inline on the done event. Pass them
          // through so the hook can attach to the finalized message.
          attachmentsSerialized:
            (parsed.data as any)?.attachmentsSerialized ||
            (parsed as any).attachmentsSerialized ||
            undefined,
          // BUG-DR-DONE-FALLBACK (2026-05-21 — follow-up to PR #198) —
          // backend mirrors its `surfaceOwnedByTool` flag so the hook can
          // suppress its bare-"Done." fallback when a surface-owning
          // tool already painted the user-visible widget.
          surfaceOwnedByTool:
            (parsed.data as any)?.surfaceOwnedByTool ??
            (parsed as any).surfaceOwnedByTool ??
            undefined,
          // BUG-DR-INLINE-REPORT-FINALIZE (2026-05-21 — follow-up to PR
          // #219) — backend ships the full Deep Research report
          // markdown body on the `done` event when a surface-owning DR
          // full-pipeline turn produced a deliverable. Frontend orphan
          // branch reads this to render the inline report above the
          // PDF + DOCX chips. Empty/undefined for non-DR turns and
          // clarifying-card turns.
          surfaceOwnedText:
            (parsed.data as any)?.surfaceOwnedText ??
            (parsed as any).surfaceOwnedText ??
            undefined,
          // ORG-USAGE-2026: non-blocking pre-block limit warning from the
          // member limit gate — published to the usage-warning channel on
          // dispatch so the banner surfaces it mid-conversation.
          usageWarning:
            (parsed.data as any)?.usageWarning ??
            (parsed as any).usageWarning ??
            undefined,
          // R11 (2026-08-04): server-side clean final text for VM SSE
          // multi-response turns. The live bubble glues per-response
          // fragments as they stream ("I'llTheTheThe"); the backend's R4
          // reassembly ships the clean text on the done event so the hook
          // can replace the glued accumulation at finalize time.
          finalTextOverride: (parsed.data as any)?.finalTextOverride ?? undefined,
          // PR-2b (2026-09-28): the jobs this reply touched, read through the one reader.
          jobStatus: readJobStatus((parsed.data as any)?.jobStatus),
          // ES-0a (2026-09-30): the server delivered the send to the room and ran no turn.
          // Dropped here until today, so the chat read it as an empty reply.
          suppressed: (parsed.data as any)?.suppressed === true || (parsed as any).suppressed === true || undefined,
          // 2026-10-08: why — so "Prest0n isn't in this thread" is said even when this browser thought he was.
          suppressedReason: typeof (parsed.data as any)?.suppressedReason === 'string'
            ? String((parsed.data as any).suppressedReason)
            : undefined,
        },
      };

    // ── OpenClaw native SSE events ──
    // The VM web-adapter proxies OpenClaw's raw SSE stream. These events use
    // OpenClaw's response API format rather than our prest0n-session format.
    // Map them so the frontend can extract text and usage without backend changes.

    case 'response.output_text.delta': {
      // OpenClaw streaming text delta
      const delta = (parsed.data as any)?.delta ?? (parsed as any).delta ?? '';
      if (typeof delta !== 'string' || delta === '') return null;
      return { type: 'text', data: { chunk: delta } };
    }

    case 'response.completed': {
      // OpenClaw turn complete — extract usage if present.
      // The VM also emits an explicit done event after this, but we handle it
      // here too as a fallback for older server.mjs versions.
      const response = (parsed as any).response ?? parsed;
      const rawUsage = response?.usage ?? (parsed as any).usage;
      let tokenUsage: Record<string, unknown> | undefined;
      if (rawUsage) {
        const outputTokens = rawUsage.output_tokens ?? rawUsage.outputTokens ?? 0;
        const inputTokensRaw = rawUsage.input_tokens ?? rawUsage.inputTokens ?? 0;
        const totalTokens = rawUsage.total_tokens ?? rawUsage.totalTokens ?? 0;
        const inputTokens = totalTokens > outputTokens ? totalTokens - outputTokens : inputTokensRaw;
        const model = response?.model || undefined;
        // Derive the provider family from the served model id — the ladder
        // routinely serves non-Claude rungs (codex/gemini/glm/grok), and the
        // old hard-coded 'openrouter' broke the provider dot color and the
        // provider-family codename fallback for every fleet turn.
        const detectedProvider = detectProviderFromModelId(model);
        // Reasoning tokens (ⓘ popup fix, Miles 2026-07-12): the fallback used
        // to map only input/output — a raw-passthrough turn lost reasoning
        // even when the gateway reported it (nested details shapes included).
        const reasoningTokens = rawUsage.reasoning_tokens ?? rawUsage.reasoningTokens
          ?? rawUsage.output_tokens_details?.reasoning_tokens
          ?? rawUsage.completion_tokens_details?.reasoning_tokens
          ?? 0;
        tokenUsage = {
          inputTokens,
          outputTokens,
          model,
          provider: detectedProvider !== 'unknown' ? detectedProvider : 'openrouter',
          ...(typeof reasoningTokens === 'number' && reasoningTokens > 0 ? { reasoningTokens } : {}),
        };
      }
      if (!tokenUsage) return null;
      return {
        type: 'done',
        data: { handled: true, tokenUsage: tokenUsage as any },
      };
    }

    case 'turn_continuing': {
      // EFax 9, B5 (2026-10-08): the ask's computer job continues out of sight, on the ask's own task.
      const taskId = typeof (parsed.data as { taskId?: unknown } | undefined)?.taskId === 'string'
        ? String((parsed.data as { taskId: string }).taskId).trim()
        : '';
      if (!taskId) return null;
      return { type: 'turn_continuing', data: { taskId } };
    }

    case 'error': {
      // INCIDENT-20260813 finding #1 (closed 2026-08-14): forward the
      // structured errorInfo the server now stamps on SSE errors. Fernando
      // strict-mode payloads arrive FLAT ({code, traceId, userMessage, …} as
      // the data object itself, endpoints.ts makeFernandoError) — synthesize
      // errorInfo from them so those long-dropped payloads finally render.
      const rawData = (parsed.data as any) || {};
      const structured = rawData.errorInfo
        || (typeof rawData.traceId === 'string' && typeof rawData.userMessage === 'string'
          ? { code: rawData.code, traceId: rawData.traceId, userMessage: rawData.userMessage, userMessageEs: rawData.userMessageEs }
          : undefined);
      return {
        type: 'error',
        data: {
          message: rawData.message || (parsed.message as string) || 'Unknown error',
          recoverable: rawData.recoverable ?? (parsed.recoverable as boolean | undefined),
          code: rawData.code || (parsed.code as string | undefined),
          errorInfo: structured,
        },
      };
    }

    default:
      console.warn('[Prest0nSession] Unknown SSE event type:', eventType);
      return null;
  }
}

// ============================================================================
// Stream Dispatcher
// ============================================================================

/**
 * Dispatch a parsed StreamEvent to the appropriate callback.
 */
function dispatchEvent(event: StreamEvent, callbacks: TurnStreamCallbacks): void {
  switch (event.type) {
    case 'thinking':
      callbacks.onThinking?.(event.data.message);
      break;

    case 'text':
      callbacks.onTextChunk?.(event.data.chunk);
      break;

    case 'tool_start':
      callbacks.onToolStart?.(event.data.toolId, event.data.label);
      break;

    case 'tool_result':
      // 2026-08-12: forward the per-tool `error` code (e.g. APP_NOT_CONNECTED)
      // — it was parsed into the event since day one but dropped here, so no
      // consumer could ever react deterministically to a failed backend tool.
      callbacks.onToolResult?.(
        event.data.toolId,
        event.data.success,
        event.data.resultHtml,
        event.data.error,
        event.data.signal,
      );
      break;

    case 'done':
      // ORG-USAGE-2026: fan the pre-block limit warning out to the banner
      // channel before the done callback — display-only, never throws.
      if (event.data.usageWarning) publishUsageWarning(event.data.usageWarning);
      callbacks.onDone?.(
        event.data.handled,
        event.data.tokenUsage,
        event.data.attachmentsSerialized,
        event.data.surfaceOwnedByTool,
        event.data.surfaceOwnedText,
        event.data.tokenUsages,
        event.data.finalTextOverride,
        event.data.jobStatus,
        event.data.suppressed,
        event.data.suppressedReason,
      );
      break;

    case 'turn_continuing':
      // EFax 9, B5: follow the ask on its own task exactly as a queued one (the poll runs on after this stream ends).
      void callbacks.onTaskQueued?.(event.data.taskId, 'running');
      break;

    case 'error':
      // Third arg was parsed-but-dropped since day one (same class as the
      // tool_result fix above) — useGlobalTools.onError has accepted and
      // rendered errorInfo (localized copy + "(ref: …)") all along.
      callbacks.onError?.(event.data.message, event.data.recoverable, event.data.errorInfo);
      break;
  }
}

// ============================================================================
// Main Parser
// ============================================================================

/**
 * Consume a ReadableStream of SSE data and dispatch typed events to callbacks.
 *
 * Handles:
 * - Chunked delivery (line buffer accumulation)
 * - Multi-line data fields
 * - Both `event:` + `data:` format and flat JSON `{type, data}` format
 * - AbortSignal for cancellation
 *
 * Resolves when the `done` or `error` event is received, or when the
 * stream ends naturally.
 */
export async function parseSSEStream(
  stream: ReadableStream<Uint8Array>,
  callbacks: TurnStreamCallbacks,
  signal?: AbortSignal
): Promise<void> {
  const decoder = new TextDecoder();
  const reader = stream.getReader();

  // Buffer for accumulating partial lines across chunks
  let lineBuffer = '';
  // Buffer for accumulating lines of a single SSE block (separated by blank lines)
  let blockBuffer = '';

  // Set up abort listener to cancel the reader mid-read.
  // Without this, reader.read() blocks indefinitely on a stalled stream
  // and never checks signal.aborted until the next chunk arrives.
  let abortHandler: (() => void) | undefined;
  if (signal) {
    abortHandler = () => {
      try { reader.cancel('aborted'); } catch { /* ignore */ }
    };
    signal.addEventListener('abort', abortHandler);
  }

  try {
    while (true) {
      if (signal?.aborted) {
          break;
      }

      const { done, value } = await reader.read();

      if (done) {
        // Process any remaining buffered data
        if (lineBuffer) {
          blockBuffer += lineBuffer + '\n';
        }
        if (blockBuffer.trim()) {
          const msg = parseSSEBlock(blockBuffer);
          if (msg) {
            const evt = parseStreamEvent(msg);
            if (evt) {
              dispatchEvent(evt, callbacks);
            }
          }
        }
        break;
      }

      // Decode the chunk and append to line buffer
      lineBuffer += decoder.decode(value, { stream: true });

      // Process all complete lines (terminated by \n)
      const lines = lineBuffer.split('\n');
      // The last element may be a partial line — keep it in the buffer
      lineBuffer = lines.pop() ?? '';

      for (const line of lines) {
        if (line === '' || line === '\r') {
          // Blank line = end of SSE block
          if (blockBuffer.trim()) {
            const msg = parseSSEBlock(blockBuffer);
            if (msg) {
              const evt = parseStreamEvent(msg);
              if (evt) {
                dispatchEvent(evt, callbacks);

                // Stop processing after done, a continuing ask, or an unrecoverable error
                if (evt.type === 'done' || evt.type === 'turn_continuing') {
                  return;
                }
                if (evt.type === 'error' && !evt.data.recoverable) {
                  return;
                }
              }
            }
            blockBuffer = '';
          }
        } else {
          blockBuffer += line + '\n';
        }
      }
    }
  } finally {
    if (abortHandler && signal) {
      signal.removeEventListener('abort', abortHandler);
    }
    try {
      reader.releaseLock();
    } catch {
      // Already released
    }
  }
}
